<script setup lang="ts">
import type { ReleasesCollectionItem } from '@nuxt/content'
import {
  RELEASE_KIND_LABELS,
  releaseAnchor,
  releaseDisplayDate,
  sortReleases,
} from '~/utils/releases'

// #348: リリースごとのユーザー向けの変更点。`/releases.xml`（Karasu が読むフィード）と
// 同じ `content/releases/*.yml` から作る。X の投稿のリンク先でもあるので OGP を持たせる
// （Karasu は画像を添付できず、X に出る絵はこのページのカードだけ）。
// 設計は doc/architecture/update_history.md。

const runtimeConfig = useRuntimeConfig()

const { data: releases } = await useAsyncData('releases', () =>
  queryCollection('releases').all() as Promise<ReleasesCollectionItem[]>,
)

const isLocal = runtimeConfig.public.environment === 'local' || runtimeConfig.public.environment === 'offline'

const visibleReleases = computed(() =>
  sortReleases((releases.value || []).filter(r => !r.draft || isLocal)),
)

const TITLE = 'アップデート情報 | Code & Magic'
const DESCRIPTION = 'Code & Magic のアップデートの記録。新しくできるようになったことや、改善したところをお知らせします。'

useSeoMeta({
  title: TITLE,
  description: DESCRIPTION,
  ogTitle: TITLE,
  ogDescription: DESCRIPTION,
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    { rel: 'canonical', href: `${runtimeConfig.public.serviceDomain}/releases` },
    { rel: 'alternate', type: 'application/rss+xml', title: 'Code & Magic アップデート情報 RSS', href: '/releases.xml' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700;800&display=swap' },
  ],
})

definePageMeta({
  layout: 'simple-landing',
})
</script>

<template>
  <div class="releases-bg">
    <div class="centered-area">
      <div class="rounded-area">
        <NuxtLink
          to="/"
          class="home-logo"
        >
          <BrandTextLogo />
        </NuxtLink>

        <div class="releases-header">
          <h1 class="releases-title">
            アップデート情報
          </h1>
          <p class="releases-subtitle">
            Code & Magic の新しくなったところ
          </p>
        </div>

        <div
          v-if="visibleReleases.length > 0"
          class="releases-list"
        >
          <section
            v-for="release in visibleReleases"
            :id="releaseAnchor(release.version)"
            :key="release.version"
            :class="['release-card', { 'release-card--draft': release.draft }]"
          >
            <div class="release-meta">
              <time class="release-date">{{ releaseDisplayDate(release.date) }}</time>
              <span class="release-version">ver. {{ release.version }}</span>
              <span
                v-if="release.draft"
                class="draft-badge"
              >非公開</span>
            </div>
            <p class="release-summary">
              {{ release.summary }}
            </p>
            <ul class="release-entries">
              <li
                v-for="(entry, i) in release.entries"
                :key="i"
                class="release-entry"
              >
                <span :class="['kind-badge', `kind-badge--${entry.kind}`]">{{ RELEASE_KIND_LABELS[entry.kind] }}</span>
                <NuxtLink
                  v-if="entry.link"
                  :to="entry.link"
                  class="entry-text entry-text--link"
                >
                  {{ entry.text }}
                </NuxtLink>
                <span
                  v-else
                  class="entry-text"
                >{{ entry.text }}</span>
              </li>
            </ul>
          </section>
        </div>

        <p
          v-else
          class="releases-empty"
        >
          まだアップデートの記録がありません。
        </p>

        <div class="releases-footer">
          <NuxtLink
            to="/news"
            class="footer-link"
          >
            ニュース
          </NuxtLink>
          <a
            href="/releases.xml"
            class="footer-link"
            target="_blank"
          >
            RSS フィード
          </a>
          <NuxtLink
            to="/"
            class="footer-link"
          >
            ホームに戻る
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '~/assets/css/landing-variables.css';

.releases-bg {
  font-family: "Noto Serif JP", "Noto Sans JP", serif;
  color: var(--landing-color-text);
  width: 100%;
  max-width: 100%;
  padding: 0;
}

.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.rounded-area {
  margin: 0 0.5em;
  padding: var(--landing-rounded-padding);
  background-color: var(--landing-color-card-warm);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
  position: relative;
  overflow: hidden;
}

.rounded-area::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 160px;
  background-image: url("/images/articles/encodia-world.webp");
  background-size: cover;
  background-position: center top;
  mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 60%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 60%, transparent 100%);
  pointer-events: none;
}

.home-logo {
  display: block;
  position: relative;
  z-index: 1;
  margin-top: -0.3rem;
  margin-bottom: 0;
}

.home-logo :deep(.shadowed) {
  height: 2.5rem;
  filter: none;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.home-logo:hover :deep(.shadowed) {
  opacity: 0.8;
}

.releases-header {
  position: relative;
  padding: 0.5rem 1rem 2rem;
  margin-bottom: 1rem;
}

.releases-title {
  color: var(--landing-color-navy);
  font-size: 2rem;
  font-weight: 900;
  text-align: center;
  margin-top: 0;
  margin-bottom: 0.3em;
}

.releases-subtitle {
  font-size: 1.05rem;
  text-align: center;
  color: #555;
  margin-bottom: 0;
}

.releases-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.release-card {
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  padding: 1rem 1.25rem;
  /* `/releases#v2-50-0` で開いたとき、カードの頭が画面の端に貼りつかないように */
  scroll-margin-top: 1rem;
}

.release-card--draft {
  border: 2px dashed #999;
}

.release-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.release-date {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--landing-color-teal-dark);
  letter-spacing: 0.04em;
}

.release-version {
  font-size: 0.7rem;
  font-weight: 600;
  color: #777;
}

.draft-badge {
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
  background-color: #999;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
}

.release-summary {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.6;
  color: var(--landing-color-navy);
  margin: 0 0 0.6rem;
}

.release-entries {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.release-entry {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  font-size: 0.9rem;
  line-height: 1.6;
}

.kind-badge {
  flex: 0 0 auto;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.05rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;
  /* 「新機能」「改善」「修正」で幅を揃え、本文の頭を縦にそろえる */
  min-width: 5em;
  text-align: center;
}

.kind-badge--new {
  color: #fff;
  background-color: var(--landing-color-teal-dark);
}

.kind-badge--improve {
  color: var(--landing-color-teal-dark);
  background-color: var(--landing-color-card-mint);
}

.kind-badge--fix {
  color: #555;
  background-color: #eee;
}

.entry-text {
  color: var(--landing-color-text);
}

.entry-text--link {
  color: var(--landing-color-teal-dark);
  text-decoration: underline;
}

.releases-empty {
  text-align: center;
  color: #555;
  padding: 3rem 1rem;
}

.releases-footer {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.footer-link {
  color: var(--landing-color-teal-dark);
  text-decoration: none;
  font-size: 0.85rem;
}

.footer-link:hover {
  text-decoration: underline;
}
</style>
